import React, { useEffect, useRef, useState } from 'react';
import axios from '../axiosConfig';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Button, Row, Col } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import Layout from './Layout';

const API_URL = process.env.REACT_APP_API_URL;

function FormCreator() {
  const formBuilderRef = useRef(null);
  const [formName, setFormName] = useState('');
  const [formIcon, setFormIcon] = useState('');
  const [selectedChildForms, setSelectedChildForms] = useState([]);
  const [iconOptions, setIconOptions] = useState([]);
  const [forms, setForms] = useState([]);
  const [availableForms, setAvailableForms] = useState([]);
  const [formType, setFormType] = useState('form'); // State to manage form type
  const history = useHistory();

  useEffect(() => {
    fetchForms();
    fetchIconOptions();
  }, []);

  useEffect(() => {
    setAvailableForms(forms.filter(f => !selectedChildForms.some(cf => cf.id === f.id)));
  }, [forms, selectedChildForms]);

  // Use another useEffect to ensure that the ref is set before initializing the builder
  useEffect(() => {
    if (formBuilderRef.current) {
      initializeFormBuilder();
    }
  }, [formBuilderRef.current, formType]);

  const fetchForms = async () => {
    try {
      const response = await axios.get(`${API_URL}/forms`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setForms(response.data);
    } catch (error) {
      toast.error('Failed to fetch forms');
    }
  };

  const fetchIconOptions = async () => {
    try {
      const response = await axios.get('/icons.json');
      setIconOptions(response.data);
    } catch (error) {
      toast.error('Failed to fetch icon options');
    }
  };

  const initializeFormBuilder = (formSchema = {}) => {
    if (!window.Formio) {
      toast.error('Formio library is not loaded');
      return;
    }
  
    if (formBuilderRef.current) {
      const options = formType === 'wizard' ? { display: 'wizard' } : {};
      window.Formio.builder(formBuilderRef.current, formSchema, options).then(builder => {
        formBuilderRef.current.builder = builder;
      }).catch(error => {
        toast.error('Error initializing the form builder');
        console.error('Formio builder initialization error:', error);
      });
    }
  };

  const handleSaveForm = async () => {
    if (!formBuilderRef.current || !formBuilderRef.current.builder) {
      toast.error('Form Builder is not initialized');
      return;
    }

    try {
      const formSchema = formBuilderRef.current.builder.schema;
      const formData = {
        name: formName,
        icon: formIcon,
        childForms: selectedChildForms.map(f => ({ id: f.id, name: f.name })),
        formSchema,
        formType
      };

      await axios.post(`${API_URL}/forms`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Form saved successfully');
      history.push('/forms');
    } catch (error) {
      toast.error('Failed to save form');
    }
  };

  const handleCancel = () => {
    history.push('/forms');
  };

  const handleFormTypeChange = (e) => {
    const formSchema = formBuilderRef.current?.builder?.schema || {};
    setFormType(e.target.value);
    if (formBuilderRef.current) {
      const options = e.target.value === 'wizard' ? { display: 'wizard' } : {};
      window.Formio.builder(formBuilderRef.current, formSchema, options).then(builder => {
        formBuilderRef.current.builder = builder;
      }).catch(error => {
        toast.error('Error re-initializing the form builder');
        console.error('Formio builder re-initialization error:', error);
      });
    }
  };

  const handleSelectChildForm = (formId) => {
    const selectedForm = forms.find(f => f.id === formId);
    if (selectedForm && !selectedChildForms.some(f => f.id === selectedForm.id)) {
      setSelectedChildForms([...selectedChildForms, selectedForm]);
      setAvailableForms(availableForms.filter(f => f.id !== selectedForm.id));
    }
  };

  const handleRemoveChildForm = (formId) => {
    const removedForm = selectedChildForms.find(f => f.id === formId);
    setSelectedChildForms(selectedChildForms.filter(f => f.id !== formId));
    setAvailableForms([...availableForms, removedForm]);
  };

  const handleMoveRight = () => {
    const selectedOptions = Array.from(document.getElementById('availableForms').selectedOptions);
    selectedOptions.forEach(o => handleSelectChildForm(parseInt(o.value)));
  };

  const handleMoveLeft = () => {
    const selectedOptions = Array.from(document.getElementById('selectedChildForms').selectedOptions);
    selectedOptions.forEach(o => handleRemoveChildForm(parseInt(o.value)));
  };

  return (
    <Layout>
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">Create Form</h1>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="form-group">
              <label>Form Name</label>
              <input
                type="text"
                className="form-control"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Form Icon</label>
              <select
                className="form-control"
                value={formIcon}
                onChange={(e) => setFormIcon(e.target.value)}
                required
              >
                <option value="">Select Icon</option>
                {iconOptions.map((icon, index) => (
                  <option key={index} data-icon={icon.value} value={icon.value}>
                    {icon.label}
                  </option>
                ))}
              </select>
            </div>
          
            <div className="form-group">
              <label>Form Type</label>
              <select
                className="form-control"
                value={formType}
                onChange={handleFormTypeChange}
                required
              >
                <option value="form">Form</option>
                <option value="wizard">Wizard</option>
              </select>
            </div>
            <div ref={formBuilderRef}></div>
            <div className="d-flex justify-content-end" style={{ marginTop: '20px' }}>
              <Button variant="secondary" onClick={handleCancel} style={{ marginRight: '10px' }}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSaveForm}>
                Save Form
              </Button>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Layout>
  );
}

export default FormCreator;
